import { LoadingState } from '@orrekie/ui';

import { useAuth } from '@orrekie/shared';
import { type ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';

import { buildProtectedLoginRedirect } from '../lib/authRedirectPath';
import { buildVerifyEmailPendingHref, requiresEmailVerification } from '../lib/emailVerificationGate';
import { PUBLIC_UVALUE_CALCULATOR_PATH } from '../lib/publicUValueCalculatorReturnPath';

function SignedInAccessDenied() {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50 px-4"
      data-testid="protected-access-denied"
    >
      <div className="max-w-md text-center space-y-4">
        <p className="text-gray-800">You do not have access to this area.</p>
        <p>
          <Link to={PUBLIC_UVALUE_CALCULATOR_PATH} className="text-blue-700 underline font-medium">
            Return to U-Value calculator
          </Link>
        </p>
      </div>
    </div>
  );
}

interface ProtectedRouteProps {
  children: ReactNode;
  requiredSystemRole?: string;
}

export default function ProtectedRoute({ children, requiredSystemRole }: ProtectedRouteProps) {
  const { ready, hasUser, user, role, systemRole } = useAuth();
  const location = useLocation();

  // Role hierarchy - higher numbers have more privileges
  const RANK = {
    superadmin: 4,
    system_superadmin: 4,
    admin: 3,
    system_admin: 3,
    org_owner: 2,
    org_admin: 2,
    org_user: 1,
    individual: 1,
    premium: 1,
    user: 1,
  };

  // Check if user role satisfies required role (hierarchy-based)
  const roleSatisfies = (required: string, actual: string | null | undefined): boolean => {
    if (!required) return true;               // if route didn't specify, any authed user is fine
    if (!actual) return false;
    if (actual === 'superadmin') return true; // superadmin satisfies all
    const rq = RANK[required as keyof typeof RANK] ?? 0;
    const ac = RANK[actual as keyof typeof RANK] ?? 0;
    return ac >= rq;
  };

  // Check if user has required role
  const hasRequiredRole = () => {
    if (!requiredSystemRole) return true;
    
    // Check both role and systemRole using hierarchy
    const ok = roleSatisfies(requiredSystemRole, role) || roleSatisfies(requiredSystemRole, systemRole);
    
    if (import.meta.env?.DEV) {
      console.info('[Guard] decision', {
        required: requiredSystemRole ?? '(none)',
        role, 
        systemRole, 
        ok, 
        path: window.location.pathname 
      });
    }
    
    return ok;
  };

  // 1. Wait for auth to be ready
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingState label="Checking access…" />
      </div>
    );
  }

  // 2. Check if user is authenticated — preserve return path for sign-in only
  if (!hasUser) {
    return (
      <Navigate
        to={buildProtectedLoginRedirect(location.pathname, location.search)}
        replace
      />
    );
  }

  // 3. Signed-in free users must verify email before registered-user routes
  if (requiresEmailVerification(user, systemRole, role)) {
    const returnPath = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={buildVerifyEmailPendingHref(returnPath)}
        replace
      />
    );
  }

  // 4. Signed-in but insufficient role — do not send to /login?next= (avoids Login auto-redirect loop)
  if (requiredSystemRole && !hasRequiredRole()) {
    return <SignedInAccessDenied />;
  }

  return children;
}
